import { PaginatedRequestParams } from '../../../../api/nm-types'

export interface FilterTemplate {
  key: string
  operator: FilterBy['operator']
  possibleValues?: (searchText: string) => Promise<{ value: string; displayName: string }[]>
  possibleValuesSync?: (searchText?: string) => { value: string; displayName: string }[]
}

export type FilterBy = IsFilter | IsOneOfFilter | MatchesFilter | DateFilter

export interface IsOneOfFilter {
  key: string // e.g. appliance
  operator: 'isOneOf'
  values: { value: string; displayName: string }[]
}

export interface IsFilter {
  key: string // e.g. tag
  operator: 'is'
  value: string
  displayName?: string
}

export interface MatchesFilter {
  key: string // e.g. name
  operator: 'matches'
  value: string
  displayName?: string
}

export interface DateFilter {
  key: string // e.g. 1970-01-01T00:00:00.000Z
  operator: 'after' | 'before'
  value: string
  displayName?: string
}

export function urlParamsToFilters<T extends PaginatedRequestParams>(
  urlParams: T,
  mapUrlParamToFilterFn: (key: keyof T, value: string) => FilterBy | undefined,
): FilterBy[] {
  const filters: FilterBy[] = []
  for (const key of Object.keys(urlParams) as Array<keyof typeof urlParams>) {
    if (!urlParams[key]) {
      continue
    }
    const filter = mapUrlParamToFilterFn(key, urlParams[key] as string)
    if (filter) {
      filters.push(filter)
    }
  }
  return filters
}

export function filtersToUrlParams<T extends PaginatedRequestParams>(
  filters: FilterBy[],
  prevFilter: FilterBy[],
  mapFilterToUrlParamFn: (filter: FilterBy) => Partial<T>,
): Omit<T, 'pageNumber' | 'rowsPerPage' | 'asc' | 'desc'> {
  function toUrlParams(filters: FilterBy[]) {
    return filters.reduce((urlParams, filter) => {
      return {
        ...urlParams,
        ...mapFilterToUrlParamFn(filter),
      }
    }, {} as T)
  }

  const removedFilters = prevFilter.filter(
    (prev) => !filters.some((current) => current.key === prev.key && current.operator === prev.operator),
  )
  const urlParams = toUrlParams(filters)
  const urlParamsToRemove = toUrlParams(removedFilters)
  for (const keyToRemove of Object.keys(urlParamsToRemove)) {
    if (!urlParams[keyToRemove as keyof typeof urlParams]) {
      ;(urlParams as any)[keyToRemove] = ''
    }
  }
  return urlParams
}
